import { Injectable } from '@angular/core';
import { catchError, first, map, Observable, of, switchMap, throwError } from 'rxjs';
import { Ticket } from '../../domain/model/ticket.model';
import {
  IApiTicketGetAllParams,
  IApiTicketGetAllQueryParams,
} from '../interfaces/ticket-api.interface';
import { TicketRepositoryAdapter } from './ticket-repository.adapter';

@Injectable()
export class TicketQueryRepositoryAdapter extends TicketRepositoryAdapter {


  override getAllTickets(
    params?: IApiTicketGetAllParams
  ): Observable<Ticket[]> {
    if (params?.queryParams) {
      return this.loadFromRemote(params.queryParams).pipe(
        switchMap(() => this.getCachedOrStorageTickets()),
        catchError(() => this.getCachedOrStorageTickets())
      );
    }

    return this.getCachedOrStorageTickets();
  }

  override getTicketById(id: string): Observable<Ticket | null> {
    return this.apiService.getById(id).pipe(
      switchMap((ticket) =>
        ticket
          ? this.mergeWithLocalTickets([ticket]).pipe(
              map((tickets) => (tickets.length > 0 ? tickets[0] : null))
            )
          : this.getCachedOrRemoteTicketById(id)
      ),
      catchError((error) => {
        this.logger.error(new Error(`Error getting ticket by id ${id}`), error);

        return this.getCachedOrRemoteTicketById(id);
      })
    );
  }

  private getCachedOrStorageTickets(): Observable<Ticket[]> {
    return this.cache
      .getCurrentTickets()
      .pipe(
        first(),
        switchMap((tickets) =>
          tickets !== null ? of(tickets) : this.loadFromStorage()
        )
      );
  }

  private getCachedOrRemoteTicketById(id: string): Observable<Ticket | null> {
    return this.cache.getTicketById(id).pipe(
      switchMap((ticket) =>
        ticket ? of(ticket) : this.storageService.getById(id)
      ),
      catchError(() => of(null))
    );
  }

  private loadFromRemote(
    queryParams?: IApiTicketGetAllQueryParams
  ): Observable<Ticket[]> {
    return this.apiService.getAll(queryParams).pipe(
      switchMap((serverTickets) => this.mergeWithLocalTickets(serverTickets)),
      catchError((error) =>
        throwError(
          () => new Error(`Error loading tickets from remote ${error}`)
        )
      )
    );
  }
}
